import React from 'react';
import { faBrain } from '@fortawesome/free-solid-svg-icons';
import BlogPost from '../BlogPost';
import provenanceImg from '../../../assets/images/blogs/provenance-by-design.png';

const ProvenanceByDesign = () => (
    <BlogPost
        title="Provenance by Design: Why Not All Language Models Are Created Equal"
        subtitle="Standard LLMs strip provenance from knowledge systematically, architecturally, and by design. Ours doesn't."
        category="Cognition"
        categoryIcon={faBrain}
        color="#003366"
        date="February 2026"
        readTime="4 min read"
        image={provenanceImg}
        imageAlt="Provenance by design - every answer traces back to source through regulation, policy, and domain knowledge"
        ctaHeading="Want to see provenance in action?"
        ctaText="Book a discovery call and we'll show you exactly how every answer traces back to source documentation."
        seoDescription="Why standard LLMs destroy knowledge provenance and how Nebulyx built a Consistentlanguage model that preserves full chain of custody for regulated industries."
        seoKeywords="provenance, ConsistentAI, language model, regulated AI, audit trail, knowledge decay, Nebulyx Cognition"
        slug="provenance-by-design"
    >
        <p>
            Standard LLMs strip provenance from knowledge systematically, architecturally, and by design.
        </p>

        <blockquote className="blog-post-quote">
            "LLMs use of encodings in weights is looses provenance  where masses amounts of
            documents get compressed, blended, and statistically distributed. They can loose the links of
            custody ie not surviving the training pipeline. The information persists while the lineage
            does not."
        </blockquote>

        <p><strong>But not all language models are created equal.</strong></p>

        <p>
            The autoregressive transformer architecture that powers most LLMs is probabilistic
            by design. Our language model doesn't follow this architecture - and that's a
            deliberate choice.
        </p>

        <h3>A Different Architecture</h3>

        <p>
            We built a Consistentlanguage model that encodes domain knowledge as graphs and
            embeddings rather than compressing it into parametric weights. The structure survives,
            the relationships survive, and critically - <strong>the provenance survives</strong>.
        </p>

        <p>
            Every answer traces back to source documentation directly. Not "where the model
            looked" but exactly which document, which passage, which rule drove the output. The
            chain of custody remains intact because the architecture was designed to preserve it.
        </p>

        <h3>How It Works in Practice</h3>

        <p>
            <strong>Decision traces (context graphs)</strong> capture full context at every
            step - so when the system makes a recommendation you can follow the reasoning back
            through the graph to the original sources.
        </p>

        <p>
            <strong>The bounded decision space</strong> means the agent cannot hallucinate
            outside documented constraints - because the bounds are derived from the sources
            and the provenance travels with the knowledge.
        </p>

        <h3>Why This Matters for Regulated Industries</h3>

        <p>
            We're running use cases in regulated environments that need to tie directly back
            to regulations to pass audit scrutiny. When a regulator asks <em>why the system
            made a decision</em>, we show exactly which constraints applied, which policy drove
            the outcome, which source documentation was considered.
        </p>

        <p>
            The knowledge network decay that Jessica describes is real - but it's a consequence
            of architectural choices, not an inevitability.
        </p>

        <h3>30 Years in the Making</h3>

        <p>
            After 30+ years of creating mission-critical decisioning systems in regulated
            environments, I went a different way. I built a language model from the ground up
            to solve these kinds of use cases.
        </p>

        <p>
            It worked so well that there is a patent filed because of it.
        </p>

        <p>
            And I chose <strong>provenance by design</strong>. 🎯
        </p>
    </BlogPost>
);

export default ProvenanceByDesign;